"use client";

import React from "react";
import SectionBadge from "./SectionBadge";
import { TextAnimate } from "./TextAnimation";
import HowItWorksStepCard from "./HowItWorksStepCard";
import { motion, useScroll, useTransform } from "motion/react";
import { useRef } from "react";
import type { HowItWorksSectionData } from "@/types/cms";
import { useSectionTracking } from "@/lib/analytics/use-section-tracking";

interface HowItWorksSectionPCProps {
  data?: HowItWorksSectionData;
}

export default function HowItWorksSectionPC({
  data,
}: HowItWorksSectionPCProps) {
  const badgeText = data?.badgeText || "How It Works";
  const title =
    data?.title ||
    "In a few simple steps, turn your attendee list into a fully automated matchmaking experience.";
  const steps = data?.steps || [];
  const sectionRef = useSectionTracking("How It Works Section", "how-it-works");
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["0 1", "1 1"],
  });

  // Dynamic transform generation based on steps count
  const stepsCount = steps.length > 0 ? steps.length : 4;
  const scrollSegment = 0.9 / stepsCount;

  // Create max 10 Y transforms (support up to 10 cards)
  const y1 = useTransform(
    scrollYProgress,
    [0.1, 0.1 + scrollSegment, 0.1 + 2 * scrollSegment],
    ["40svh", "0svh", `${-(stepsCount - 1) * (10 / stepsCount)}svh`],
  );
  const y2 = useTransform(
    scrollYProgress,
    [
      0.1 + scrollSegment * 0.8,
      0.1 + 2 * scrollSegment,
      0.1 + 3 * scrollSegment,
    ],
    ["100svh", "0svh", `${-(stepsCount - 2) * (10 / stepsCount)}svh`],
  );
  const y3 = useTransform(
    scrollYProgress,
    [
      0.1 + 2 * scrollSegment * 0.8,
      0.1 + 3 * scrollSegment,
      0.1 + 4 * scrollSegment,
    ],
    ["100svh", "0svh", `${-(stepsCount - 3) * (10 / stepsCount)}svh`],
  );
  const y4 = useTransform(
    scrollYProgress,
    [
      0.1 + 3 * scrollSegment * 0.8,
      0.1 + 4 * scrollSegment,
      0.1 + 5 * scrollSegment,
    ],
    ["100svh", "0svh", `${-(stepsCount - 4) * (10 / stepsCount)}svh`],
  );
  const y5 = useTransform(
    scrollYProgress,
    [0.1 + 4 * scrollSegment * 0.8, 0.1 + 5 * scrollSegment],
    ["100svh", "0svh"],
  );
  const y6 = useTransform(
    scrollYProgress,
    [0.1 + 5 * scrollSegment * 0.8, 0.1 + 6 * scrollSegment],
    ["100svh", "0svh"],
  );
  const y7 = useTransform(
    scrollYProgress,
    [0.1 + 6 * scrollSegment * 0.8, 0.1 + 7 * scrollSegment],
    ["100svh", "0svh"],
  );
  const y8 = useTransform(
    scrollYProgress,
    [0.1 + 7 * scrollSegment * 0.8, 0.1 + 8 * scrollSegment],
    ["100svh", "0svh"],
  );
  const y9 = useTransform(
    scrollYProgress,
    [0.1 + 8 * scrollSegment * 0.8, 0.1 + 9 * scrollSegment],
    ["100svh", "0svh"],
  );
  const y10 = useTransform(
    scrollYProgress,
    [0.1 + 9 * scrollSegment * 0.8, 0.1 + 10 * scrollSegment],
    ["100svh", "0svh"],
  );

  // Create scale transforms
  const scale1 = useTransform(
    scrollYProgress,
    [0.1 + scrollSegment, 0.1 + 2 * scrollSegment],
    [1, 0.8],
  );
  const scale2 = useTransform(
    scrollYProgress,
    [0.1 + 2 * scrollSegment, 0.1 + 3 * scrollSegment],
    [1, 0.8 + (1 / Math.max(stepsCount - 1, 1)) * 0.13],
  );
  const scale3 = useTransform(
    scrollYProgress,
    [0.1 + 3 * scrollSegment, 0.1 + 4 * scrollSegment],
    [1, 0.8 + (2 / Math.max(stepsCount - 1, 1)) * 0.13],
  );
  const scale4 = useTransform(
    scrollYProgress,
    [0.1 + 4 * scrollSegment, 0.1 + 5 * scrollSegment],
    [1, 0.8 + (3 / Math.max(stepsCount - 1, 1)) * 0.13],
  );
  const scale5 = useTransform(
    scrollYProgress,
    [0.1 + 5 * scrollSegment, 0.1 + 6 * scrollSegment],
    [1, 0.8 + (4 / Math.max(stepsCount - 1, 1)) * 0.13],
  );
  const scale6 = useTransform(
    scrollYProgress,
    [0.1 + 6 * scrollSegment, 0.1 + 7 * scrollSegment],
    [1, 0.8 + (5 / Math.max(stepsCount - 1, 1)) * 0.13],
  );
  const scale7 = useTransform(
    scrollYProgress,
    [0.1 + 7 * scrollSegment, 0.1 + 8 * scrollSegment],
    [1, 0.8 + (6 / Math.max(stepsCount - 1, 1)) * 0.13],
  );
  const scale8 = useTransform(
    scrollYProgress,
    [0.1 + 8 * scrollSegment, 0.1 + 9 * scrollSegment],
    [1, 0.8 + (7 / Math.max(stepsCount - 1, 1)) * 0.13],
  );
  const scale9 = useTransform(
    scrollYProgress,
    [0.1 + 9 * scrollSegment, 0.1 + 10 * scrollSegment],
    [1, 0.8 + (8 / Math.max(stepsCount - 1, 1)) * 0.13],
  );

  // Map to arrays based on stepsCount
  const yTransforms = [y1, y2, y3, y4, y5, y6, y7, y8, y9, y10].slice(
    0,
    stepsCount,
  );
  const scaleTransforms = [
    scale1,
    scale2,
    scale3,
    scale4,
    scale5,
    scale6,
    scale7,
    scale8,
    scale9,
    undefined,
  ].slice(0, stepsCount);
  return (
    <section
      id="how-it-works"
      ref={(el: HTMLElement | null) => {
        (ref as React.MutableRefObject<HTMLElement | null>).current = el;
        (sectionRef as React.MutableRefObject<HTMLElement | null>).current = el;
      }}
      className="bg-black py-16 px-4 lg:px-16 font-host-grotesk flex flex-col md:h-[500svh]"
    >
      <div className="md:sticky gap-16 md:top-0 md:h-svh">
        <div className="max-w-360 mx-auto flex flex-col items-center text-center mb-14">
          <SectionBadge text={badgeText} className="mb-12" />
          <TextAnimate
            animation="blurInUp"
            by="line"
            className="text-white max-w-220 font-host-grotesk text-4xl md:text-5xl lg:text-5xl"
            as="h2"
            duration={0.2}
            once={true}
          >
            {title}
          </TextAnimate>
        </div>
        <motion.div className="relative max-w-330 w-full mx-auto">
          {steps.length > 0 ? (
            steps.map((step, index) => {
              const lines = [
                step.descriptionLine1,
                step.descriptionLine2,
              ].filter(Boolean) as string[];

              return (
                <HowItWorksStepCard
                  key={step.id}
                  step={index + 1}
                  title={step.title}
                  lines={lines}
                  imageSrc={
                    step.image?.url ||
                    `/landing-stuff/HowItsWork-${index + 1}.png`
                  }
                  imageClassName={undefined}
                  className={`absolute`}
                  style={{
                    y: yTransforms[index],
                    scale: scaleTransforms[index],
                    zIndex: index + 1,
                  }}
                />
              );
            })
          ) : (
            <>
              <HowItWorksStepCard
                step={1}
                title="Upload your attendee list"
                lines={[
                  "Full name, email, and company name.",
                  "That is all you need to start.",
                ]}
                imageSrc="/landing-stuff/HowItsWork-1.png"
                imageClassName="scale-100"
                className="absolute z-1"
                style={{ y: yTransforms[0], scale: scaleTransforms[0] }}
              />
              <HowItWorksStepCard
                step={2}
                title="AI creates live profiles"
                lines={[
                  "Our AI enriches each attendee with public information",
                  "and builds dynamic profiles in real time.",
                ]}
                imageSrc="/landing-stuff/HowItsWork-2.png"
                className="absolute z-2"
                style={{ y: yTransforms[1], scale: scaleTransforms[1] }}
              />
              <HowItWorksStepCard
                step={3}
                title="Set access levels"
                lines={[
                  "Define who can see and connect with whom across groups",
                  "like speakers, exhibitors, and general attendees.",
                ]}
                imageClassName="scale-150"
                imageSrc="/landing-stuff/HowItsWork-3.png"
                className="absolute z-3"
                style={{ y: yTransforms[2], scale: scaleTransforms[2] }}
              />
              <HowItWorksStepCard
                step={4}
                title="Launch your branded experience"
                lines={[
                  "Distribute your web and mobile app under your conference",
                  "brand and watch engagement rise.",
                ]}
                imageClassName="scale-150"
                imageSrc="/landing-stuff/HowItsWork-4.png"
                className="absolute z-4"
                style={{ y: yTransforms[3], scale: scaleTransforms[3] }}
              />
            </>
          )}
        </motion.div>
      </div>
    </section>
  );
}
